import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ArrowIcon from '../../../assets/icons/arrow_down.svg?react';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import {
  CreateMalfunction,
  setCreateMalfunctionDialog,
  useCreateMalfunctionDialog,
} from '../../../components/CreateMalfunction';
import { DelayUnmount } from '../../../components/DelayUnmount';
import { Spinner } from '../../../components/Spinner';
import {
  useCurrentAnswer,
  useCurrentQuestion,
  useInspectionPages,
} from '../../../services/inspection';
import { useLatestMalfunctions } from '../../../services/malfunctions';
import { QuestionTitle } from '../views';
import styles from './MalfunctionQuestion.module.css';
import { MalfunctionAnswerState } from '../types';
import { MalfunctionsList } from '../MalfunctionsList';
import { MalfunctionAccordionTitle, MalfunctionItem } from './views';

const MAX_MALFUNCTIONS = 10;

export const MalfunctionQuestion: FC = () => {
  const question = useCurrentQuestion();
  const { current } = useInspectionPages();
  const open = useCreateMalfunctionDialog();
  const { running, result } = useLatestMalfunctions(3);
  const [expanded, setExpanded] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const { malfunctions } = useCurrentAnswer() as MalfunctionAnswerState;

  useEffect(() => {
    if (malfunctions.length === 1) {
      setExpanded(false);
    }
  }, [malfunctions.length]);

  return (
    <>
      <Card className={styles.card}>
        <QuestionTitle index={current + 1}>{question.text}</QuestionTitle>
        {malfunctions.length < MAX_MALFUNCTIONS ? (
          <Button onClick={() => setCreateMalfunctionDialog(true)}>
            <FormattedMessage id="Malfunction.Inspection.New" />
          </Button>
        ) : (
          <FormattedMessage id="Malfunction.Inspection.MaxMalfunctions" />
        )}
        <DelayUnmount unmount={!open}>
          <CreateMalfunction
            open={open}
            onClose={() => setCreateMalfunctionDialog(false)}
          />
        </DelayUnmount>
        <DelayUnmount unmount={!listOpen}>
          <MalfunctionsList
            open={listOpen}
            onClose={() => setListOpen(false)}
          />
        </DelayUnmount>
      </Card>

      {malfunctions.length > 0 && (
        <Card className={styles.card}>
          <MalfunctionAccordionTitle>
            <FormattedMessage
              id="Malfunction.Inspection.Registered"
              values={{ count: malfunctions.length }}
            />
          </MalfunctionAccordionTitle>
          {malfunctions.map((malfunction) => (
            <MalfunctionItem key={malfunction.id}>
              {malfunction.name}
            </MalfunctionItem>
          ))}
        </Card>
      )}

      <div className={styles.malfunctionsContainer}>
        {running && <Spinner className={styles.spinner} />}
        {result && (
          <div className={styles.accordion}>
            <MalfunctionAccordionTitle
              onClick={() => setExpanded((expanded) => !expanded)}
            >
              <FormattedMessage id="Malfunction.Inspection.Latest" />
              <ArrowIcon
                className={clsx(styles.icon, !expanded && styles.flipped)}
              />
            </MalfunctionAccordionTitle>
            <div className={clsx(!expanded && styles.closed)}>
              {result?.items.map((item) => (
                <MalfunctionItem key={item.id}>{item.name}</MalfunctionItem>
              ))}
              <Button
                variant="text"
                compact
                className={styles.seeFullList}
                onClick={() => setListOpen(true)}
              >
                <FormattedMessage id="Malfunction.Inspection.SeeFull" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
